import { z } from "zod";

export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

// Screenshots, documents and exports. No video or archives: they are large and hard to review safely.
export const ALLOWED_FILE_TYPES: Record<string, readonly string[]> = {
  "image/png": ["png"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/gif": ["gif"],
  "image/webp": ["webp"],
  "application/pdf": ["pdf"],
  "text/plain": ["txt", "log"],
  "text/csv": ["csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
};

// Keys look like uploads/<owner>/<uuid>/<name>, where <owner> comes from ownerPrefix.
const KEY_PATTERN = /^uploads\/[0-9a-f]{16}\/[0-9a-f-]{36}\/[^/]{1,100}$/;

const extensionOf = (name: string): string => name.split(".").pop()?.toLowerCase() ?? "";

// Browsers report odd types for some files (a .csv from Excel is often application/vnd.ms-excel), so fall back to the extension.
export function contentTypeFor(file: { name: string; type: string }): string {
  if (file.type in ALLOWED_FILE_TYPES) {
    return file.type;
  }
  const extension = extensionOf(file.name);
  return Object.entries(ALLOWED_FILE_TYPES).find(([, extensions]) => extensions.includes(extension))?.[0] ?? file.type;
}

// Keeps the extension and drops anything that could confuse a path, a header or Jira's own file handling.
export function sanitizeFileName(name: string): string {
  const cleaned = name
    .replace(/[^A-Za-z0-9._ -]+/g, "_")
    .replace(/\s+/g, " ")
    .replace(/^[.\s]+/, "")
    .trim();
  if (cleaned.length <= 100) {
    return cleaned || "file";
  }
  const extension = extensionOf(cleaned);
  const suffix = extension && extension.length <= 10 ? `.${extension}` : "";
  return `${cleaned.slice(0, 100 - suffix.length)}${suffix}`;
}

const fileFields = {
  name: z.string().trim().min(1).max(100),
  contentType: z.string().refine((type) => type in ALLOWED_FILE_TYPES, "This type of file is not allowed"),
  size: z.number().int().min(1, "This file is empty").max(MAX_FILE_BYTES, "Files can be at most 10 MB"),
};

const hasAllowedExtension = (file: { name: string; contentType: string }): boolean =>
  (ALLOWED_FILE_TYPES[file.contentType] ?? []).includes(extensionOf(file.name));
const extensionMessage = { message: "The file name does not match its type", path: ["name"] };

// What the browser asks for before it uploads a file. The name is cleaned and shortened afterwards.
export const uploadRequestSchema = z
  .object({ ...fileFields, name: z.string().trim().min(1).max(255) })
  .refine(hasAllowedExtension, extensionMessage);

// What a submission stores for a file that was uploaded.
export const fileRefSchema = z
  .object({ ...fileFields, key: z.string().regex(KEY_PATTERN) })
  .refine(hasAllowedExtension, extensionMessage);

export type FileRef = z.infer<typeof fileRefSchema>;

// Names the files get on the Jira ticket. Two files can share a name, so repeats are numbered.
export function attachmentNames(files: readonly { name: string }[]): string[] {
  const seen = new Map<string, number>();
  return files.map(({ name }) => {
    const count = (seen.get(name) ?? 0) + 1;
    seen.set(name, count);
    if (count === 1) {
      return name;
    }
    const dot = name.lastIndexOf(".");
    return dot > 0 ? `${name.slice(0, dot)} (${count})${name.slice(dot)}` : `${name} (${count})`;
  });
}
